import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Stack,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  IconButton,
  Button,
  FormControl,
  Autocomplete,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import TuneIcon from "@mui/icons-material/Tune";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { NumericFormat } from "react-number-format";

import GeneralContext from "../GeneralContext/GeneralContext";
import SnackbarContext from "../GeneralContext/SnackbarContext";
import axios from "../../api/axios";

import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import InputFile from "../GeneralComponents/InputFile";
import BreadcrumbStandard from "../GeneralComponents/BreadcrumbStandard";
import PageButtonBlack from "../GeneralComponents/PageButtonBlack";

const titleStepSx = {
  fontWeight: 700,
  textAlign: "center",
  fontSize: "0.9rem",
};

const bodyStepSx = {
  textAlign: "center",
  fontSize: "0.78rem",
  color: "rgba(0,0,0,0.72)",
  maxWidth: "46rem",
  margin: "0 auto",
  lineHeight: 1.45,
};

const headCellSx = {
  fontWeight: 700,
  fontSize: "0.68rem",
  color: "rgba(0,0,0,0.62)",
  whiteSpace: "normal",
  lineHeight: 1.1,
  paddingTop: "0.65rem",
  paddingBottom: "0.65rem",
};

const emptyCellSx = {
  height: 92,
  color: "rgba(0,0,0,0.42)",
  fontSize: "0.76rem",
  textAlign: "center",
};

const selectedStep2RowSx = {
  backgroundColor: "#F4D8CF",
};

const modifiedSummaryRowSx = {
  backgroundColor: "#FFF4D6",
};

const replacementSummaryRowSx = {
  backgroundColor: "#E8F5E9",
};

const disabledPrimaryButtonSx = {
  opacity: 1,
  backgroundColor: "#9DB7DC !important",
  color: "#fff !important",
};

const infoBoxSx = {
  mt: 1.5,
  mb: 0.5,
  mx: "auto",
  width: "fit-content",
  minWidth: { xs: "100%", sm: 420 },
  maxWidth: 760,
  border: "2px solid #5A9DF8",
  borderRadius: "6px",
  px: 2,
  py: 1.1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  color: "#2B4A6F",
  backgroundColor: "#fff",
  textAlign: "center",
};

const step3HeaderSx = {
  ...headCellSx,
  backgroundColor: "#F5F5F5",
};

const step3RemoveCellSx = {
  backgroundColor: "#FFFFFF",
  width: 40,
  verticalAlign: "middle",
};

const step3OriginalCellSx = {
  backgroundColor: "#E9CEC6",
  verticalAlign: "middle",
};

const step3ReplacementCellSx = {
  backgroundColor: "#E3E3E3",
  verticalAlign: "middle",
};

const step3DataCellSx = {
  backgroundColor: "#DDE6F2",
  verticalAlign: "middle",
};

const step3MotivoCellSx = {
  backgroundColor: "#DDE6F2",
  verticalAlign: "middle",
  minWidth: 220,
};

const step3TextareaSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#EEF2F7",
    borderRadius: "8px",
    alignItems: "center",
  },
};

function EmptyTableRow({ colSpan, text, height = 92 }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} align="center" sx={{ ...emptyCellSx, height }}>
        {text}
      </TableCell>
    </TableRow>
  );
}

function estadoPagoLabel(v) {
  if (v === "Facturar") return "Facturando";
  if (v === "Devengar") return "Devengando";
  if (v === "Facturando") return "Facturando";
  if (v === "Devengando") return "Devengando";
  return "No Facturando";
}

function estadoServicioLabel(v) {
  if (v === "Activo" || v === "Alta" || v === "Conectado") return "Alta";
  if (v === "NYA" || v === "En proceso") return "En proceso";
  return "Baja";
}

function servicioColor(v) {
  const label = estadoServicioLabel(v);
  if (label === "Alta") return "#41AE43";
  if (label === "En proceso") return "#ABABB5";
  return "#FB3B3B";
}

function formatDateLabel(value) {
  if (!value) return "-";
  try {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
    return String(value).slice(0, 10);
  } catch {
    return String(value).slice(0, 10);
  }
}

function mapOficioOption(item) {
  return {
    ...item,
    id:
      item?.id_oficio ??
      item?.id ??
      `${item?.tipo_oficio}-${item?.numero_oficio}-${item?.fecha_oficio}`,
    numero_oficio: String(item?.numero_oficio ?? ""),
    fecha_oficio: item?.fecha_oficio ?? null,
    label: `${item?.numero_oficio ?? ""}`,
  };
}

function normalizeRectificacionData(data = {}) {
  return {
    ...data,
    rbd: String(data?.rbd ?? ""),
    id_zona: data?.id_zona ?? "",
    convocatoria: data?.convocatoria ?? "",
    operador: data?.nombre_operador ?? data?.operador ?? "",
    estado_pago: data?.estado_pago ?? "",
    estado_servicio: data?.servicio_conectividad ?? data?.estado_servicio ?? "",
    nombre_colegio: data?.nombre_colegio ?? "",
    fecha_alta: data?.fecha_alta ?? null,
    fecha_baja: data?.fecha_baja ?? null,
  };
}

function buildEmptyRectificacionRow(originalRow) {
  return {
    rbd_original: String(originalRow?.rbd ?? ""),
    id_zona_original: originalRow?.id_zona ?? "",
    convocatoria_original: originalRow?.convocatoria ?? "",
    operador_original: originalRow?.operador ?? "",
    estado_pago_original: originalRow?.estado_pago ?? "",
    estado_servicio_original: originalRow?.estado_servicio ?? "",
    motivo_rectificacion: originalRow?.motivo_baja ?? "",

    fecha_alta_original: originalRow?.fecha_alta ?? null,
    fecha_baja_original: originalRow?.fecha_baja ?? null,

    rbd_reemplazo: "",
    rbd_reemplazo_input: "",
    rbd_options: [],
    rbd_loading: false,
    rbd_error: false,
    rbd_error_msg: "",
    rbd_required_error: false,

    id_zona_reemplazo: "",
    convocatoria_reemplazo: "",
    operador_reemplazo: "",
    estado_pago_reemplazo: "",
    estado_servicio_reemplazo: "",
    nombre_colegio_reemplazo: "",
    fecha_alta_reemplazo: null,
    fecha_baja_reemplazo: null,

    replacement_loaded: false,
  };
}

export default function ParqueRectificacion() {
  const { pageTitle, setPageTitle, setDrawerHidding } = useContext(GeneralContext);
  const openSnackbar = useContext(SnackbarContext);

  const [loadingInit, setLoadingInit] = useState(true);
  const [loadingOficio, setLoadingOficio] = useState(false);
  const [loadingSend, setLoadingSend] = useState(false);

  const [oficiosInicio, setOficiosInicio] = useState([]);
  const [oficiosBaja, setOficiosBaja] = useState([]);

  const [selectedOficioInicio, setSelectedOficioInicio] = useState(null);
  const [selectedOficioBaja, setSelectedOficioBaja] = useState(null);

  const [oficioSeleccionado, setOficioSeleccionado] = useState(null);
  const [oficioRbds, setOficioRbds] = useState([]);

  const [rectificaciones, setRectificaciones] = useState([]);

  const [formData, setFormData] = useState({
    numero_oficio_rectificacion: "",
  });

  const [rectificacionFile, setRectificacionFile] = useState();
  const [evErrorFile, setEvErrorFile] = useState(false);
  const [evErrorNumero, setEvErrorNumero] = useState(false);

  const fileRef = useRef(null);

  const links = [
    { name: "Estado de Parque", path: "/parque_lista" },
    { name: "Rectificación", path: "/parque_rectificacion" },
  ];

  useEffect(() => {
    setPageTitle("Rectificación");
  }, [pageTitle, setPageTitle]);

  useEffect(() => {
    setDrawerHidding(false);
  }, [setDrawerHidding]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoadingInit(true);

        const [inicioResp, bajaResp] = await Promise.all([
          axios.getOficioPorTipos("INICIO_SERVICIO"),
          axios.getOficioPorTipos("BAJA"),
        ]);

        if (!alive) return;

        const inicioPayload = inicioResp?.data ?? inicioResp;
        const bajaPayload = bajaResp?.data ?? bajaResp;

        const inicio = Array.isArray(inicioPayload?.oficios)
          ? inicioPayload.oficios.map(mapOficioOption)
          : [];

        const baja = Array.isArray(bajaPayload?.oficios)
          ? bajaPayload.oficios.map(mapOficioOption)
          : [];

        setOficiosInicio(inicio);
        setOficiosBaja(baja);
      } catch {
        if (!alive) return;
        setOficiosInicio([]);
        setOficiosBaja([]);
        openSnackbar?.("No se pudo cargar la data inicial.", "error");
      } finally {
        if (alive) setLoadingInit(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [openSnackbar]);

  const setFileRectificacion = (archivo) => {
    setEvErrorFile(!archivo);
    setRectificacionFile((prev) => ({
      ...prev,
      archivo,
    }));
  };

  const resetLoadedData = () => {
    setOficioSeleccionado(null);
    setOficioRbds([]);
    setRectificaciones([]);
  };

  const mapDetalleOficioRows = (rows = []) => {
    return rows.map((row) => ({
      rbd: String(row?.rbd ?? ""),
      id_zona: row?.id_zona ?? "",
      convocatoria: row?.convocatoria ?? "",
      operador: row?.operador ?? row?.nombre_operador ?? "",
      estado_pago: row?.estado_pago ?? "",
      estado_servicio: row?.estado_servicio ?? row?.servicio_conectividad ?? "",
      motivo_baja: row?.motivo_baja ?? row?.motivo ?? "",
      fecha_alta: row?.fecha_alta ?? null,
      fecha_baja: row?.fecha_baja ?? null,
    }));
  };

  const getOficioFromPayload = (payload, selected, tipo) => {
    if (payload?.oficio) {
      return {
        ...payload.oficio,
        tipo_oficio: payload.oficio?.tipo_oficio ?? tipo,
      };
    }

    if (Array.isArray(payload?.oficios) && payload.oficios.length > 0) {
      const match =
        payload.oficios.find(
          (o) =>
            String(o?.id_oficio ?? o?.id ?? "") === String(selected?.id ?? "") ||
            (String(o?.numero_oficio ?? "") === String(selected?.numero_oficio ?? "") &&
              String(o?.tipo_oficio ?? "") === String(tipo))
        ) ?? payload.oficios[0];

      return {
        ...match,
        tipo_oficio: match?.tipo_oficio ?? tipo,
      };
    }

    return {
      ...selected,
      tipo_oficio: selected?.tipo_oficio ?? tipo,
    };
  };

  const getRbdsFromPayload = (payload, oficio) => {
    if (Array.isArray(payload?.rbds)) {
      return payload.rbds;
    }

    if (Array.isArray(payload?.oficios)) {
      const match =
        payload.oficios.find(
          (o) =>
            String(o?.id_oficio ?? o?.id ?? "") ===
              String(oficio?.id_oficio ?? oficio?.id ?? "") ||
            (String(o?.numero_oficio ?? "") === String(oficio?.numero_oficio ?? "") &&
              String(o?.tipo_oficio ?? "") === String(oficio?.tipo_oficio ?? ""))
        ) ?? payload.oficios[0];

      return Array.isArray(match?.rbds) ? match.rbds : [];
    }

    return [];
  };

  const handleCargarDatosOficio = async (tipo, event) => {
    event?.preventDefault();
    event?.stopPropagation();

    const selected = tipo === "INICIO_SERVICIO" ? selectedOficioInicio : selectedOficioBaja;

    if (!selected?.numero_oficio) {
      openSnackbar?.("Debe seleccionar un oficio.", "warning");
      return;
    }

    try {
      setLoadingOficio(true);

      const response = await axios.getOficioRectificacionDetalle(selected.numero_oficio, tipo);
      const payload = response?.data ?? response;

      const oficio = getOficioFromPayload(payload, selected, tipo);
      const rbds = getRbdsFromPayload(payload, oficio);

      setOficioSeleccionado(oficio);
      setOficioRbds(mapDetalleOficioRows(rbds));
      setRectificaciones([]);

      if (!rbds.length) {
        openSnackbar?.("El oficio no tiene RBD asociados.", "warning");
      }
    } catch {
      resetLoadedData();
      openSnackbar?.("No se pudo cargar la información del oficio.", "error");
    } finally {
      setLoadingOficio(false);
    }
  };

  const handleToggleOldRbd = (row) => {
    const rbd = String(row?.rbd ?? "");

    setRectificaciones((prev) => {
      const exists = prev.some((x) => String(x.rbd_original) === rbd);

      if (exists) {
        return prev.filter((x) => String(x.rbd_original) !== rbd);
      }

      return [...prev, buildEmptyRectificacionRow(row)];
    });
  };

  const handleRemoveRectificacion = (index) => {
    setRectificaciones((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRectificacion = (index, patch) => {
    setRectificaciones((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], ...patch };
      return copy;
    });
  };

  const searchReplacementOptions = async (index, value) => {
    updateRectificacion(index, {
      rbd_reemplazo_input: value || "",
      rbd_error: false,
      rbd_error_msg: "",
      rbd_required_error: false,
    });

    if (!value || !String(value).trim()) {
      updateRectificacion(index, {
        rbd_options: [],
        replacement_loaded: false,
      });
      return;
    }

    updateRectificacion(index, { rbd_loading: true });

    try {
      const response = await axios.getRBD(value);
      const payload = response?.data ?? response;

      updateRectificacion(index, {
        rbd_options: Array.isArray(payload?.rbd_list) ? payload.rbd_list : [],
        rbd_loading: false,
        rbd_error: false,
        rbd_error_msg: "",
      });
    } catch {
      updateRectificacion(index, {
        rbd_options: [],
        rbd_loading: false,
        rbd_error: true,
        rbd_error_msg: "Colegio no encontrado.",
        replacement_loaded: false,
      });
    }
  };

  const loadReplacementByRbd = async (index, rbdValue) => {
    const rbd = String(rbdValue || "").trim();

    if (!rbd) {
      updateRectificacion(index, {
        rbd_reemplazo: "",
        id_zona_reemplazo: "",
        convocatoria_reemplazo: "",
        operador_reemplazo: "",
        estado_pago_reemplazo: "",
        estado_servicio_reemplazo: "",
        nombre_colegio_reemplazo: "",
        fecha_alta_reemplazo: null,
        fecha_baja_reemplazo: null,
        replacement_loaded: false,
        rbd_error: false,
        rbd_error_msg: "",
        rbd_required_error: true,
      });
      return;
    }

    updateRectificacion(index, {
      rbd_reemplazo: rbd,
      rbd_reemplazo_input: rbd,
      rbd_loading: true,
      rbd_error: false,
      rbd_error_msg: "",
      rbd_required_error: false,
      replacement_loaded: false,
    });

    try {
      const response = await axios.getEesRectificacion(rbd);
      const data = normalizeRectificacionData(response?.data ?? response);

      updateRectificacion(index, {
        rbd_reemplazo: data.rbd,
        rbd_reemplazo_input: data.rbd,
        id_zona_reemplazo: data.id_zona,
        convocatoria_reemplazo: data.convocatoria,
        operador_reemplazo: data.operador,
        estado_pago_reemplazo: data.estado_pago,
        estado_servicio_reemplazo: data.estado_servicio,
        nombre_colegio_reemplazo: data.nombre_colegio,
        fecha_alta_reemplazo: data.fecha_alta,
        fecha_baja_reemplazo: data.fecha_baja,
        replacement_loaded: true,
        rbd_loading: false,
        rbd_error: false,
        rbd_error_msg: "",
        rbd_required_error: false,
      });
    } catch {
      updateRectificacion(index, {
        id_zona_reemplazo: "",
        convocatoria_reemplazo: "",
        operador_reemplazo: "",
        estado_pago_reemplazo: "",
        estado_servicio_reemplazo: "",
        nombre_colegio_reemplazo: "",
        fecha_alta_reemplazo: null,
        fecha_baja_reemplazo: null,
        replacement_loaded: false,
        rbd_loading: false,
        rbd_error: true,
        rbd_error_msg: "Colegio no encontrado.",
      });
    }
  };

  const handleReplacementInputChange = (index, value) => {
    updateRectificacion(index, {
      rbd_reemplazo_input: value || "",
      rbd_reemplazo: value || "",
      replacement_loaded: false,
      rbd_required_error: false,
      id_zona_reemplazo: "",
      convocatoria_reemplazo: "",
      operador_reemplazo: "",
      estado_pago_reemplazo: "",
      estado_servicio_reemplazo: "",
      nombre_colegio_reemplazo: "",
      fecha_alta_reemplazo: null,
      fecha_baja_reemplazo: null,
    });

    searchReplacementOptions(index, value);
  };

  const handleReplacementSelect = (index, newValue) => {
    const value = newValue || "";
    updateRectificacion(index, {
      rbd_reemplazo: value,
      rbd_reemplazo_input: value,
      rbd_required_error: !String(value).trim(),
    });
    loadReplacementByRbd(index, value);
  };

  const handleMotivoChange = (index, value) => {
    updateRectificacion(index, {
      motivo_rectificacion: value,
    });
  };

  const getGlobalInfoMessage = () => {
    const tipo = oficioSeleccionado?.tipo_oficio;
    if (!rectificaciones.length) return "";

    if (tipo === "BAJA") {
      const fechas = [
        ...new Set(
          rectificaciones
            .map((row) => formatDateLabel(row?.fecha_baja_original))
            .filter((x) => x && x !== "-")
        ),
      ];

      if (fechas.length === 1) {
        return `Los RBD seleccionados quedarán en BAJA con fecha ${fechas[0]}`;
      }

      return "Los RBD seleccionados quedarán en BAJA utilizando la fecha de baja del oficio original para cada registro";
    }

    const fechas = [
      ...new Set(
        rectificaciones
          .map((row) => formatDateLabel(row?.fecha_alta_original))
          .filter((x) => x && x !== "-")
      ),
    ];

    if (fechas.length === 1) {
      return `Los RBD seleccionados quedarán en ALTA con fecha ${fechas[0]}`;
    }

    return "Los RBD seleccionados quedarán en ALTA utilizando la fecha de alta del oficio original para cada registro";
  };

  const allReplacementSelected = useMemo(() => {
    if (!rectificaciones.length) return false;

    return rectificaciones.every(
      (row) => String(row?.rbd_reemplazo || "").trim() && row?.replacement_loaded
    );
  }, [rectificaciones]);

  const canSubmit = useMemo(() => {
    const hasRows = rectificaciones.length > 0;

    const allRowsValid =
      hasRows &&
      rectificaciones.every(
        (row) => String(row?.rbd_reemplazo || "").trim() && row?.replacement_loaded
      );

    const numeroOk = String(formData?.numero_oficio_rectificacion || "").trim() !== "";
    const fileOk = !!rectificacionFile?.archivo;

    return hasRows && allRowsValid && numeroOk && fileOk;
  }, [rectificaciones, formData, rectificacionFile]);

  const resumenRows = useMemo(() => {
    if (!oficioRbds.length) return [];

    const rectMap = new Map(rectificaciones.map((r) => [String(r.rbd_original), r]));
    const tipo = oficioSeleccionado?.tipo_oficio;
    const rows = [];

    oficioRbds.forEach((orig) => {
      const match = rectMap.get(String(orig.rbd));

      if (!match) {
        rows.push({
          row_type: "original_unmodified",
          highlight: false,
          rbd: orig.rbd,
          id_zona: orig.id_zona,
          convocatoria: orig.convocatoria,
          operador: orig.operador,
          estado_pago: orig.estado_pago,
          estado_servicio: orig.estado_servicio,
          fecha_baja: orig.fecha_baja || "-",
          fecha_inicio_servicio: orig.fecha_alta || "-",
          motivo: orig.motivo_baja || "-",
        });
        return;
      }

      const originalFechaAlta = match.fecha_alta_original ?? orig.fecha_alta ?? "-";
      const originalFechaBaja = match.fecha_baja_original ?? orig.fecha_baja ?? "-";
      const nuevaFechaAlta = match.fecha_alta_reemplazo ?? "-";
      const nuevaFechaBaja = match.fecha_baja_reemplazo ?? "-";

      if (tipo === "BAJA") {
        rows.push({
          row_type: "original_modified",
          highlight: true,
          rbd: match.rbd_original,
          id_zona: match.id_zona_original,
          convocatoria: match.convocatoria_original,
          operador: match.operador_original,
          estado_pago: match.estado_pago_original,
          estado_servicio: "Alta",
          fecha_baja: "-",
          fecha_inicio_servicio: originalFechaAlta || "-",
          motivo: match.motivo_rectificacion || "Rectificación de oficio",
        });

        rows.push({
          row_type: "replacement",
          highlight: true,
          rbd: match.rbd_reemplazo || "-",
          id_zona: match.id_zona_reemplazo || "-",
          convocatoria: match.convocatoria_reemplazo || "-",
          operador: match.operador_reemplazo || "-",
          estado_pago: match.estado_pago_reemplazo || "-",
          estado_servicio: "Baja",
          fecha_baja: originalFechaBaja || "-",
          fecha_inicio_servicio: nuevaFechaAlta || "-",
          motivo: match.motivo_rectificacion || "Rectificación de oficio",
        });
      } else {
        rows.push({
          row_type: "original_modified",
          highlight: true,
          rbd: match.rbd_original,
          id_zona: match.id_zona_original,
          convocatoria: match.convocatoria_original,
          operador: match.operador_original,
          estado_pago: match.estado_pago_original,
          estado_servicio: "Baja",
          fecha_baja: originalFechaBaja || "-",
          fecha_inicio_servicio: "-",
          motivo: match.motivo_rectificacion || "Rectificación de oficio",
        });

        rows.push({
          row_type: "replacement",
          highlight: true,
          rbd: match.rbd_reemplazo || "-",
          id_zona: match.id_zona_reemplazo || "-",
          convocatoria: match.convocatoria_reemplazo || "-",
          operador: match.operador_reemplazo || "-",
          estado_pago: match.estado_pago_reemplazo || "-",
          estado_servicio: "Alta",
          fecha_baja: nuevaFechaBaja || "-",
          fecha_inicio_servicio: originalFechaAlta || "-",
          motivo: match.motivo_rectificacion || "Rectificación de oficio",
        });
      }
    });

    return rows;
  }, [oficioRbds, rectificaciones, oficioSeleccionado]);

  const validateBeforeSend = () => {
    if (!rectificaciones.length) {
      openSnackbar?.("Debe seleccionar al menos un RBD a rectificar.", "warning");
      return false;
    }

    const updatedRows = rectificaciones.map((row) => ({
      ...row,
      rbd_required_error: !String(row.rbd_reemplazo || "").trim() || !row.replacement_loaded,
    }));
    setRectificaciones(updatedRows);

    const invalidReplacement = updatedRows.some(
      (row) => !String(row.rbd_reemplazo || "").trim() || !row.replacement_loaded
    );
    if (invalidReplacement) {
      openSnackbar?.(
        "En el paso 3 debe seleccionar y validar el RBD de reemplazo para todas las filas cargadas.",
        "warning"
      );
      return false;
    }

    const invalidNumero = !String(formData.numero_oficio_rectificacion || "").trim();
    setEvErrorNumero(invalidNumero);
    if (invalidNumero) {
      openSnackbar?.("Debe ingresar el número de oficio de rectificación.", "warning");
      return false;
    }

    if (!rectificacionFile?.archivo) {
      setEvErrorFile(true);
      fileRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      openSnackbar?.("Debe adjuntar el PDF del oficio de rectificación.", "warning");
      return false;
    }

    return true;
  };

  const handleSend = async (event) => {
    event.preventDefault();

    if (!validateBeforeSend()) return;

    try {
      setLoadingSend(true);

      const payload = {
        oficio_origen: oficioSeleccionado,
        numero_oficio_rectificacion: formData.numero_oficio_rectificacion,
        rectificaciones: rectificaciones.map((row) => ({
          rbd_original: row.rbd_original,
          rbd_reemplazo: row.rbd_reemplazo,
          motivo_rectificacion: row.motivo_rectificacion,
          original: {
            id_zona: row.id_zona_original,
            convocatoria: row.convocatoria_original,
            operador: row.operador_original,
            estado_pago: row.estado_pago_original,
            estado_servicio: row.estado_servicio_original,
            fecha_alta: row.fecha_alta_original,
            fecha_baja: row.fecha_baja_original,
          },
          reemplazo: {
            id_zona: row.id_zona_reemplazo,
            convocatoria: row.convocatoria_reemplazo,
            operador: row.operador_reemplazo,
            estado_pago: row.estado_pago_reemplazo,
            estado_servicio: row.estado_servicio_reemplazo,
            fecha_alta: row.fecha_alta_reemplazo,
            fecha_baja: row.fecha_baja_reemplazo,
          },
        })),
      };

      const form = new FormData();
      form.append("rectificacion_data", JSON.stringify(payload));
      form.append("file", rectificacionFile.archivo, "rectificacion.pdf");

      await axios.putRectificacionOficio(form);

      openSnackbar?.("Rectificación ingresada correctamente.", "success");

      setSelectedOficioInicio(null);
      setSelectedOficioBaja(null);
      resetLoadedData();
      setFormData({
        numero_oficio_rectificacion: "",
      });
      setRectificacionFile(null);
      setEvErrorFile(false);
      setEvErrorNumero(false);
    } catch {
      openSnackbar?.("No se pudo guardar la rectificación.", "error");
    } finally {
      setLoadingSend(false);
    }
  };

  if (loadingInit) {
    return (
      <PageBody>
        <BreadcrumbStandard links={links} />
        <PageBgBlock>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
              width: "100%",
              backgroundColor: "white",
            }}
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ marginTop: 2 }}>
              Cargando...
            </Typography>
          </Box>
        </PageBgBlock>
      </PageBody>
    );
  }

  return (
    <PageBody>
      <BreadcrumbStandard links={links} />

      <PageBgBlock>
        <form onSubmit={handleSend} id="parque_rectificacion">
          <Stack spacing={2}>
            <Typography
              variant="h1"
              color="inherit"
              noWrap
              sx={{ flexGrow: 1, textAlign: "center", fontWeight: 700 }}
            >
              RECTIFICACIÓN
            </Typography>

            <Box
              justifyContent="center"
              sx={{
                maxWidth: "100%",
                margin: "0 auto",
                display: "flex",
              }}
            >
              <Typography
                variant="body"
                align="center"
                sx={{ marginBottom: "1rem", maxWidth: "34rem" }}
              >
                Si se necesita rectificar un oficio por error del documento por favor siga los
                pasos a continuación:
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" sx={titleStepSx}>
              PASO 1
            </Typography>

            <Box>
              <Typography variant="body2" sx={bodyStepSx}>
                Elegir el tipo de oficio que necesita rectificar y buscar por su número, luego
                cargar los datos.
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Grid container spacing={2} alignItems="flex-start">
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={1} alignItems="flex-start">
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={oficiosInicio}
                    value={selectedOficioInicio}
                    onChange={(_, newValue) => setSelectedOficioInicio(newValue)}
                    getOptionLabel={(option) => (option?.numero_oficio ? `${option.numero_oficio}` : "")}
                    isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Buscar oficio de Inicio de Servicio"
                        variant="outlined"
                      />
                    )}
                  />

                  <Button
                    type="button"
                    variant="text"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={(event) => handleCargarDatosOficio("INICIO_SERVICIO", event)}
                    sx={{
                      color: "#1565C0",
                      fontSize: "0.78rem",
                      textTransform: "none",
                      padding: 0,
                      minWidth: "auto",
                    }}
                  >
                    Cargar datos de oficio
                  </Button>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={1} alignItems="flex-start">
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={oficiosBaja}
                    value={selectedOficioBaja}
                    onChange={(_, newValue) => setSelectedOficioBaja(newValue)}
                    getOptionLabel={(option) => (option?.numero_oficio ? `${option.numero_oficio}` : "")}
                    isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Buscar oficio de Baja"
                        variant="outlined"
                      />
                    )}
                  />

                  <Button
                    type="button"
                    variant="text"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={(event) => handleCargarDatosOficio("BAJA", event)}
                    sx={{
                      color: "#1565C0",
                      fontSize: "0.78rem",
                      textTransform: "none",
                      padding: 0,
                      minWidth: "auto",
                    }}
                  >
                    Cargar datos de oficio
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" sx={titleStepSx}>
              PASO 2
            </Typography>

            <Box>
              <Typography variant="body2" sx={bodyStepSx}>
                Marque con checkbox los RBD que necesita rectificar. Si un RBD queda desmarcado,
                no se realizará rectificación sobre ese registro.
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <TableContainer component={Paper} sx={{ mb: 1, mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCellSx, width: 44 }} />
                    <TableCell sx={headCellSx}>RBD</TableCell>
                    <TableCell sx={headCellSx}>ID zona</TableCell>
                    <TableCell sx={headCellSx}>Convocatoria</TableCell>
                    <TableCell sx={headCellSx}>Operador</TableCell>
                    <TableCell sx={headCellSx}>Estado de Pago</TableCell>
                    <TableCell sx={headCellSx}>Estado de servicio</TableCell>
                    <TableCell sx={headCellSx}>Motivo de baja</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loadingOficio ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={26} />
                      </TableCell>
                    </TableRow>
                  ) : !oficioRbds.length ? (
                    <EmptyTableRow
                      colSpan={8}
                      text="Aquí se cargarán los RBD y sus datos cargados en el oficio seleccionado"
                    />
                  ) : (
                    oficioRbds.map((row) => {
                      const checked = rectificaciones.some(
                        (x) => String(x.rbd_original) === String(row.rbd)
                      );

                      return (
                        <TableRow key={row.rbd} hover sx={checked ? selectedStep2RowSx : undefined}>
                          <TableCell>
                            <Checkbox checked={checked} onChange={() => handleToggleOldRbd(row)} />
                          </TableCell>
                          <TableCell>{row.rbd}</TableCell>
                          <TableCell>{row.id_zona}</TableCell>
                          <TableCell>{row.convocatoria}</TableCell>
                          <TableCell>{row.operador}</TableCell>
                          <TableCell>{estadoPagoLabel(row.estado_pago)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <FiberManualRecordIcon
                                sx={{
                                  fontSize: "0.75rem",
                                  color: servicioColor(row.estado_servicio),
                                }}
                              />
                              {estadoServicioLabel(row.estado_servicio)}
                            </Box>
                          </TableCell>
                          <TableCell>{row.motivo_baja || "-"}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" sx={titleStepSx}>
              PASO 3
            </Typography>

            <Box>
              <Typography variant="body2" sx={bodyStepSx}>
                Aquí se cargarán los RBD seleccionados en el paso anterior. Se debe dejar el RBD de
                reemplazo en el campo correspondiente y al hacerlo se cargarán los datos de este
                RBD. El motivo de la rectificación es opcional.
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <TableContainer component={Paper} sx={{ mb: 0.5, mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...step3HeaderSx, width: 40 }}>
                      <IconButton
                        size="small"
                        onClick={() => setRectificaciones([])}
                        disabled={!rectificaciones.length}
                        type="button"
                      >
                        <RemoveCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell sx={step3HeaderSx}>RBD a rectificar</TableCell>
                    <TableCell sx={step3HeaderSx}>RBD de reemplazo</TableCell>
                    <TableCell sx={step3HeaderSx}>ID zona</TableCell>
                    <TableCell sx={step3HeaderSx}>Convocatoria</TableCell>
                    <TableCell sx={step3HeaderSx}>Operador</TableCell>
                    <TableCell sx={step3HeaderSx}>Estado de Pago</TableCell>
                    <TableCell sx={step3HeaderSx}>Estado de servicio</TableCell>
                    <TableCell sx={step3HeaderSx}>Motivo de Rectificación</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!rectificaciones.length ? (
                    <EmptyTableRow colSpan={9} text="Aquí se cargará el o los RBD de reemplazo" />
                  ) : (
                    rectificaciones.map((row, index) => (
                      <TableRow key={`${row.rbd_original}-${index}`}>
                        <TableCell sx={step3RemoveCellSx}>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveRectificacion(index)}
                            type="button"
                          >
                            <RemoveCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>

                        <TableCell sx={step3OriginalCellSx}>{row.rbd_original}</TableCell>

                        <TableCell sx={{ ...step3ReplacementCellSx, minWidth: 210 }}>
                          <FormControl sx={{ minWidth: "100%" }}>
                            <Autocomplete
                              value={row.rbd_reemplazo || ""}
                              inputValue={row.rbd_reemplazo_input || ""}
                              onChange={(_, newValue) => handleReplacementSelect(index, newValue)}
                              onInputChange={(_, newValue) =>
                                handleReplacementInputChange(index, newValue)
                              }
                              options={row.rbd_options || []}
                              loading={row.rbd_loading}
                              loadingText="Cargando..."
                              freeSolo={false}
                              size="small"
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  error={row.rbd_error || row.rbd_required_error}
                                  helperText={
                                    row.rbd_required_error
                                      ? "Debe seleccionar un RBD de reemplazo"
                                      : row.rbd_error
                                        ? row.rbd_error_msg
                                        : row.rbd_loading
                                          ? "Cargando..."
                                          : ""
                                  }
                                  placeholder="Buscar RBD"
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                      <>
                                        {params.InputProps.startAdornment}
                                        <TuneIcon />
                                      </>
                                    ),
                                  }}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      backgroundColor: "#FFFFFF",
                                      borderRadius: "8px",
                                      alignItems: "center",
                                    },
                                  }}
                                />
                              )}
                            />
                          </FormControl>
                        </TableCell>

                        <TableCell sx={step3DataCellSx}>{row.id_zona_reemplazo || "-"}</TableCell>
                        <TableCell sx={step3DataCellSx}>{row.convocatoria_reemplazo || "-"}</TableCell>
                        <TableCell sx={step3DataCellSx}>{row.operador_reemplazo || "-"}</TableCell>
                        <TableCell sx={step3DataCellSx}>
                          {row.estado_pago_reemplazo
                            ? estadoPagoLabel(row.estado_pago_reemplazo)
                            : "-"}
                        </TableCell>
                        <TableCell sx={step3DataCellSx}>
                          {row.estado_servicio_reemplazo ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <FiberManualRecordIcon
                                sx={{
                                  fontSize: "0.75rem",
                                  color: servicioColor(row.estado_servicio_reemplazo),
                                }}
                              />
                              {estadoServicioLabel(row.estado_servicio_reemplazo)}
                            </Box>
                          ) : (
                            "-"
                          )}
                        </TableCell>

                        <TableCell sx={step3MotivoCellSx}>
                          <TextField
                            multiline
                            minRows={2}
                            maxRows={4}
                            size="small"
                            sx={step3TextareaSx}
                            value={row.motivo_rectificacion}
                            onChange={(e) => handleMotivoChange(index, e.target.value)}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {!!rectificaciones.length && (
            <Box sx={{width: "100%"}}>
              <Box sx={infoBoxSx}>
                <InfoOutlinedIcon sx={{ fontSize: "1rem", color: "#3E8AE8" }} />
                <Typography
                  sx={{
                    fontSize: "0.76rem",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  {getGlobalInfoMessage()}
                </Typography>
              </Box>
              </Box>
            )}

            {!!rectificaciones.length && !allReplacementSelected && (
              <Typography
                variant="caption"
                sx={{
                  textAlign: "center",
                  color: "#C62828",
                  display: "block",
                  mt: 0.5,
                }}
              >
                Debe seleccionar un RBD de reemplazo válido para todas las filas cargadas en el paso 3.
              </Typography>
            )}

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" sx={titleStepSx}>
              PASO 4
            </Typography>

            <Box>
              <Typography variant="body2" sx={bodyStepSx}>
                Dejar los datos del oficio de rectificación.
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Grid container spacing={2} alignItems="flex-start">
              <Grid size={{ xs: 12, md: 4 }}>
                <NumericFormat
                  customInput={TextField}
                  label="N° de oficio de Rectificación"
                  value={formData.numero_oficio_rectificacion}
                  onValueChange={(newValue) => {
                    setEvErrorNumero(false);
                    setFormData((prev) => ({
                      ...prev,
                      numero_oficio_rectificacion: newValue.value,
                    }));
                  }}
                  allowNegative={false}
                  size="small"
                  error={evErrorNumero}
                  helperText={evErrorNumero ? "Debe ingresar el número de oficio" : ""}
                  sx={{ width: "100%" }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Box ref={fileRef}>
                  <InputFile
                    onFileSelect={setFileRectificacion}
                    fileType="pdf"
                    placeholder="Oficio de Rectificación"
                    buttonText="Subir archivo"
                    sizeBtnGrid={3}
                  />
                  {evErrorFile && (
                    <Typography variant="caption" sx={{ color: "red" }}>
                      Debe adjuntar oficio de rectificación en PDF
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" sx={titleStepSx}>
              RESUMEN
            </Typography>

            <Box>
              <Typography variant="body2" sx={bodyStepSx}>
                A continuación podrá revisar el detalle de los estados en que quedarán los RBD. Las filas amarillas corresponden a
                RBD modificados y las verdes a los RBD de reemplazo.
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <TableContainer component={Paper} sx={{ mb: 1, mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>RBD</TableCell>
                    <TableCell sx={headCellSx}>ID zona</TableCell>
                    <TableCell sx={headCellSx}>Convocatoria</TableCell>
                    <TableCell sx={headCellSx}>Operador</TableCell>
                    <TableCell sx={headCellSx}>Estado de Pago</TableCell>
                    <TableCell sx={headCellSx}>Estado de servicio</TableCell>
                    <TableCell sx={headCellSx}>Fecha de baja</TableCell>
                    <TableCell sx={headCellSx}>Fecha de Inicio servicio</TableCell>
                    <TableCell sx={headCellSx}>Motivo</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!resumenRows.length ? (
                    <EmptyTableRow
                      colSpan={9}
                      text="Aquí se cargarán los RBD y sus datos correctos posterior a la rectificación"
                    />
                  ) : (
                    resumenRows.map((row, idx) => (
                      <TableRow
                        key={`${row.rbd}-${idx}`}
                        sx={
                          row.row_type === "replacement"
                            ? replacementSummaryRowSx
                            : row.highlight
                              ? modifiedSummaryRowSx
                              : undefined
                        }
                      >
                        <TableCell>{row.rbd}</TableCell>
                        <TableCell>{row.id_zona || "-"}</TableCell>
                        <TableCell>{row.convocatoria || "-"}</TableCell>
                        <TableCell>{row.operador || "-"}</TableCell>
                        <TableCell>
                          {row.estado_pago !== "-" ? estadoPagoLabel(row.estado_pago) : "-"}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <FiberManualRecordIcon
                              sx={{
                                fontSize: "0.75rem",
                                color: servicioColor(row.estado_servicio),
                              }}
                            />
                            {estadoServicioLabel(row.estado_servicio)}
                          </Box>
                        </TableCell>
                        <TableCell>{row.fecha_baja || "-"}</TableCell>
                        <TableCell>{row.fecha_inicio_servicio || "-"}</TableCell>
                        <TableCell>{row.motivo || "-"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ margin: "auto", marginTop: "1.5rem" }}>
              <PageButtonBlack
                form="parque_rectificacion"
                loading={loadingSend}
                disabled={!canSubmit}
                sx={!canSubmit ? disabledPrimaryButtonSx : undefined}
              >
                Hacer rectificación
              </PageButtonBlack>
            </Box>
          </Stack>
        </form>
      </PageBgBlock>
    </PageBody>
  );
}